import { createSelector } from "reselect";

import { EntityType } from "@mapmycustomers/shared/enum";
import {
  RouteCompanyStop,
  RouteManualStop,
  RoutePersonStop,
  RoutePreviewStop,
} from "@mapmycustomers/shared/types/entity/Route";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import { RootState } from "@app/store/rootReducer";

import { EditorRouteStop } from "../types/RouteStop";
import correlateManualStopsToPreview from "../utils/correlateManualStopsToPreview";
import reorderStopsByPreview from "../utils/reorderStopsByPreview";
import routeStopToPreviewStop from "../utils/routeStopToPreviewStop";

type PreviewFields = {
  arrivalTime?: string;
  scheduledTime?: null | string;
  scheduledTimeSkipped?: boolean;
  travelDistance?: number;
  travelTime?: number;
};

const routeViewState = (state: RootState) => state.scene.route.routeDetails.view;

export const getRoute = createSelector(routeViewState, ({ route }) => route);
export const getRoutePreview = createSelector(routeViewState, ({ routePreview }) => routePreview);
export const isRouteViewInitializing = createSelector(
  routeViewState,
  ({ initializing }) => initializing
);

export const getIsRouteSharing = (state: RootState) => state.scene.route.routeDetails.view.sharing;

export const getRouteUpdating = (state: RootState) =>
  state.scene.route.routeDetails.view.routeUpdating;

export const getIsRoutePreviewStale = (state: RootState) =>
  state.scene.route.routeDetails.view.isPreviewStale;

export const getRouteStops = createSelector(
  [getRoute, getRoutePreview],
  (route, routePreview): EditorRouteStop[] => {
    const allStops = (route?.stopTime ?? []).filter(
      (stop): stop is RouteCompanyStop | RouteManualStop | RoutePersonStop =>
        stop.entity === EntityType.COMPANY ||
        stop.entity === EntityType.PERSON ||
        stop.entity === null
    );
    if (!allStops.length) {
      return [];
    }

    const previewByCompanyId = new Map<number, PreviewFields>();
    const previewByPersonId = new Map<number, PreviewFields>();
    (routePreview?.stopTime ?? []).forEach((previewStop) => {
      const fields = {
        arrivalTime: previewStop.arrivalTime,
        scheduledTime: previewStop.scheduledTime,
        scheduledTimeSkipped: previewStop.scheduledTimeSkipped,
        travelDistance: previewStop.travelDistance,
        travelTime: previewStop.travelTime,
      };
      if (previewStop.entity === EntityType.COMPANY) {
        previewByCompanyId.set(previewStop.accountId, fields);
      } else if (previewStop.entity === EntityType.PERSON) {
        previewByPersonId.set(previewStop.contactId, fields);
      }
    });
    // Manual stops pair positionally against the preview's `originalIndex`, which indexes the
    // request — and the request excludes checked-off stops. Walking them here would shift every
    // later manual stop by one.
    const manualMatches = correlateManualStopsToPreview(
      allStops.filter((stop) => !stop.checkedOff),
      routePreview?.stopTime
    );

    // Layer preview-derived fields in the persisted route order. Manual stops are
    // paired positionally against the preview's `originalIndex`, so this MUST run
    // before any reorder to keep the pairing correct.
    const mergedStops = allStops.map((stop, index) => {
      const preview =
        stop.entity === EntityType.COMPANY
          ? previewByCompanyId.get(stop.accountId)
          : stop.entity === EntityType.PERSON
            ? previewByPersonId.get(stop.contactId)
            : isDefined(stop.id)
              ? manualMatches.get(stop.id)
              : undefined;
      return {
        ...stop,
        arrivalTime: preview?.arrivalTime,
        displayOrder: stop.displayOrder ?? index,
        scheduledTime: stop.scheduledTime ?? preview?.scheduledTime ?? null,
        scheduledTimeSkipped: preview?.scheduledTimeSkipped,
        travelDistance: preview?.travelDistance,
        travelTime: preview?.travelTime,
      };
    });

    // Reorder to the optimized preview order (arrival-time order) so the sidebar
    // matches the map's stop markers, which already number by preview order.
    return reorderStopsByPreview(mergedStops, routePreview);
  }
);

// The route's own stops as RoutePreviewStop[], for the map to draw when no usable preview
// exists — before the route's first Build, and while an edit has left the last one stale.
// Built from getRouteStops so the map's numbering keeps matching the sidebar's, and checked-off
// stops are kept: they still need their check pin, which RouteStopFeature derives separately
// from the stop list it receives alongside these.
export const getRouteViewPreviewStops = createSelector(
  [getRouteStops],
  (stops): RoutePreviewStop[] =>
    stops.reduce<RoutePreviewStop[]>((previewStops, stop) => {
      const previewStop = routeStopToPreviewStop(stop, previewStops.length);
      if (previewStop) {
        previewStops.push(previewStop);
      }
      return previewStops;
    }, [])
);
